import { createContext } from 'react';

import _ from 'lodash';
import { getIntl, history } from 'umi';

import { bindServices } from '@/services/api';
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';
import { Regexp } from '@/utils/infrastructure/utilities';
import CoreQueries from '@/utils/input/queries';
import {
  commandMessageType,
  commandResponseMessage,
  filtersConfig,
  iterationResultsFiltersConfig,
} from './utils';

// Bind API services to namespace
const api = bindServices('applications');

export const RequestContext = createContext({});

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const notices = {
  approve: false,
  reject: false,
  complete: false,
  executeTask: false,
  sign: false,
  cancel: false,
  void: false,
  sendNotifications: false,
  evaluateApprovers: false,
  backToStep: false,
  reassignTask: false,
  executeAutomaticAction: false,
  history: false,
  continue: false,
  autoAssignTask: false,
};

const initialState = {
  list: [],
  autoOpenEditOperation: false,
  currentItem: null,
  selectItem: null,
  pagination: meta,
  actionTypeResultPagination: meta,
  emailEventTraceResultPagination: { ...meta, limit: 10 },
  completeFormConfig: null,
  editTableRowVisible: false,
  tableRecord: null,
  continueValidationErrors: [],
  instanceActionType: null,
  customActiveTab: null,
  executedCommand: null,
  currentExecutedActionInstanceId: null,
  stepStatusChanged: null,
  filtersConfig,
  iterationResultsFiltersConfig,
  // bulk request item
  bulkTaskItemData: [],
  bulkTaskItemPagination: meta,
  // email event trace
  emailEventTrace: undefined,
  ...notices,
};

const Model = {
  namespace: 'applications',
  state: initialState,
  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        let res = null;
        const { reqOrigin, applicationStatus, page, limit, ...params } = payload;

        switch (reqOrigin) {
          case CoreEnums.ProcessInstanceRequestOrigin.All:
          case CoreEnums.ProcessInstanceRequestOrigin.MyRequests:
          case CoreEnums.ProcessInstanceRequestOrigin.ReceivedRequests:
          case CoreEnums.ProcessInstanceRequestOrigin.RevisionRequests:
            const fragments = [reqOrigin, applicationStatus].filter((v) => !_.isNil(v)).join('/');
            res = yield call(api.getAll, {
              route: `processInstance/requests/${fragments}`,
              page,
              limit,
              ...CoreQueries.Requests.List,
              ...params,
            });
            break;
          default:
            res = yield call(api.getAll, {
              route: 'bulkRequestTask/paged',
              page,
              limit,
              ...CoreQueries.Requests.BulkUpload,
              ...params,
            });
            break;
        }

        yield put({
          type: 'queryApplications',
          payload: { ...res, page, limit },
        });
      } catch (e) {}
    },

    *fetchById({ payload }, { call, put, select }) {
      const { processInstanceId, actionInstanceId, versionHistoryMatch } = payload;
      try {
        const endPoint = versionHistoryMatch
          ? `history/${processInstanceId}/${actionInstanceId}`
          : `getDetails/${processInstanceId}`;

        const data = yield call(api.getAll, { route: `processInstance/${endPoint}`, ...payload });
        const autoOpen = yield select((o) => o.applications.autoOpenEditOperation);

        yield put({
          type: 'queryById',
          payload: {
            data,
            autoOpenEditOperation: !!(
              data?.process?.autoOpenEditOperation &&
              (autoOpen || Boolean(+sessionStorage.getItem('x-autoOpenEditOperation')))
            ),
          },
        });
      } catch (e) {
        if (e?.aborted) return;
        const route = _.split(history.location.pathname, '/')
          .filter((str) => !Regexp.isUUID(str))
          .join('/');

        history.replace(route);
      }
    },

    *fetchBulkTaskById({ payload }, { call, put }) {
      try {
        const data = yield call(api.getById, {
          route: 'bulkRequestTask',
          ...payload,
          ...CoreQueries.Requests.BulkTaskDetail,
        });
        yield put({ type: 'queryById', payload: { data, bulk: true } });
      } catch (e) {
        if (!(e instanceof Response)) return;
        if (!history.location.pathname.includes('bulkRequestTask')) return;
        history.replace(
          `/applications?requestOrigin=${CoreEnums.ProcessInstanceRequestOrigin.BulkRequests}`,
        );
      }
    },

    *fetchBulkTaskItem({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'bulkRequestTaskItem/paged',
          ...payload,
          ...CoreQueries.Requests.BulkTaskPaged,
        });
        yield put({
          type: 'queryBulkTaskItem',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *instanceStatusChanged({ payload }, { put }) {
      yield put({ type: 'queryInstanceStatusChanged', payload });
    },

    *download({ payload }, { call }) {
      try {
        const res = yield call(api.getById, {
          route: `formInstanceFieldAttachment/download`,
          ...payload,
        });
        const blob = new Blob([res], { type: 'application/octetstream' });
        const url = window.URL || window.webkitURL;
        const link = url.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('download', payload.fileName);
        a.setAttribute('href', link);
        a.click();
        url.revokeObjectURL(link);
      } catch (e) {}
    },

    *exeCommand({ payload }, { call, put, select }) {
      let success = false;
      try {
        const { formatMessage } = getIntl();

        const messageType = commandMessageType[payload.command] || 'info';
        const { name } = yield select((state) => state.applications.currentItem);

        yield call(api.exec, payload);
        yield put({ type: 'queryExec', payload: { command: payload.command } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: messageType,
            ...(messageType && {
              message: formatMessage(
                { id: 'applications.text.message.success' },
                {
                  name,
                  action: formatMessage({ id: commandResponseMessage[payload.command] }),
                },
              ),
            }),
          },
        });
        success = true;
      } catch (e) {
      } finally {
        // Pass success so UI can keep action buttons locked until the step advances.
        payload.callback?.(success);
      }
    },

    *fetchCompleteInfo({ payload }, { call, put }) {
      try {
        const res = yield call(api.exec, payload);
        yield put({ type: 'queryComplete', payload: res });
      } catch (e) {
        payload.callback?.();
      }
    },

    *complete({ payload }, { call, put }) {
      try {
        const fragments = [
          payload?.processInstanceId,
          payload?.actionInstanceId,
          payload?.formInstanceId,
          payload?.operationType,
        ].filter(Boolean);

        yield call(api.create, {
          route: `processEngine/continue/${fragments.join('/')}`,
          ..._.omit(payload, [
            'processInstanceId',
            'actionInstanceId',
            'formInstanceId',
            'operationType',
          ]),
        });

        yield put({
          type: 'queryExec',
          payload: {
            ..._.pick(payload, ['autoOpenEditOperation', 'actionInstanceId']),
            command: CoreEnums.RequestDetailCommandType.Continue,
            complete: true,
          },
        });
        // Detail → continue skips tabs cleanup, so Forms cache would keep pre-save
        // values (autocalculado stale until full refresh). Force a fresh fetch.
        yield put({ type: 'applicationTabs/invalidateTab', payload: { tab: 'forms' } });
        yield put({ type: 'applicationTabs/invalidateTab', payload: { tab: 'variables' } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'info' },
        });
      } catch (e) {
        if (e?.validationErrors) {
          yield put({ type: 'queryCompleteError', payload: e });
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }
      }
    },

    *fetchEmailEventTrace({ payload }, { call, put }) {
      try {
        const res = yield call(api.getData, payload);
        yield put({ type: 'queryEventTrace', payload: res });
      } catch (e) {}
    },

    *customActiveTab({ payload }, { put }) {
      yield put({ type: 'queryCustomActiveTab', payload });
    },

    *consumeSequence({ payload }, { call, put }) {
      try {
        const res = yield call(api.getData, payload);
        // yield put({ type: '', payload: res });
      } catch (e) {}
    },

    *instanceActionType({ payload }, { put }) {
      yield put({ type: 'queryInstanceActionType', payload });
      if (!payload) {
        const initial = { items: [], ...meta };
        yield put({ type: 'queryEmailEventTraceResult', payload: initial });
      }
    },

    *actionTypeResult({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, payload);
        // Normalize shape so reducer can distinguish list vs single item
        const normalized = {
          totalCount: 0,
          ...(_.isArray(res?.items) ? res : {}),
          ...(!_.isArray(res?.items) ? { item: res } : {}),
          ..._.pick(payload, ['page', 'limit', 'source']),
        };
        yield put({ type: 'queryActionTypeResult', payload: normalized });
      } catch (e) {}
    },

    *emailEventTraceResult({ payload }, { call, put }) {
      try {
        const {
          processInstanceId,
          actionInstanceId,
          emailMessageTraceId,
          page,
          limit,
          orderBy = 'created asc',
        } = payload;

        const pathSegments = [processInstanceId, actionInstanceId].filter(Boolean);
        let route = `processInstance/data/emailTrackingEvents/${pathSegments.join('/')}`;
        if (emailMessageTraceId) {
          route += `?emailMessageTraceId=${encodeURIComponent(emailMessageTraceId)}`;
        }

        const res = yield call(api.getAll, {
          route,
          page,
          limit,
          orderBy,
        });

        yield put({
          type: 'queryEmailEventTraceResult',
          payload: {
            totalCount: 0,
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *toggleEditTableRowVisible({ payload }, { put }) {
      yield put({ type: 'queryEditTableRowVisible', payload });
    },

    *toggleAutoOpenEditEnabled({ payload }, { put }) {
      yield put({ type: 'queryAutoOpenEditEnabled', payload });
    },

    *cleanupCurrentItem(o, { put }) {
      yield put({ type: 'cleanupCurrent' });
    },

    *cleanupSelectItem(o, { put }) {
      yield put({ type: 'cleanupSelect' });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'queryCleanup', payload });
    },
  },
  reducers: {
    queryApplications(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        executedCommand: null,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryById(state, { payload }) {
      const { instance, process, versionId, ...bulkData } = payload.data || {};
      const currentActionInstanceId =
        instance?.currentActionInstanceId ||
        instance?.currentActionInstance?.id ||
        _.last(instance?.actionInstances)?.id;

      return {
        ...state,
        executedCommand: null,
        autoOpenEditOperation: payload.autoOpenEditOperation,
        currentItem: {
          ...instance,
          process,
          versionId,
          ...(payload.bulk && { ...bulkData }),
          commands: _.orderBy(instance?.commands, ['customCommandType'], ['asc']),
          currentActionInstanceId,
          // Tag commands with the step they belong to (avoids cross-step flashes).
          commandsActionInstanceId: currentActionInstanceId || null,
        },
      };
    },

    queryBulkTaskItem(state, { payload }) {
      return {
        ...state,
        bulkTaskItemData: payload.items,
        bulkTaskItemPagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryExec(state, { payload }) {
      return {
        ...state,
        complete: !!payload.complete,
        executedCommand: payload.command,
        autoOpenEditOperation: payload.autoOpenEditOperation,
        ...(payload.actionInstanceId && {
          currentExecutedActionInstanceId: payload.actionInstanceId,
        }),
      };
    },

    queryExecError(state, { payload }) {
      return {
        ...state,
        continueValidationErrors: payload,
      };
    },

    queryComplete(state, { payload }) {
      return {
        ...state,
        autoOpenEditOperation: false,
        currentExecutedActionInstanceId: null,
        completeFormConfig: {
          ...payload,
          behaviorCases: formFieldBehaviorsMapping(payload),
        },
      };
    },

    queryCompleteError(state, { payload }) {
      return {
        ...state,
        continueValidationErrors: payload?.validationErrors || [],
      };
    },

    queryEditTableRowVisible(state, { payload }) {
      return {
        ...state,
        editTableRowVisible: payload?.state ?? false,
        tableRecord: payload?.record ?? null,
      };
    },

    queryInstanceStatusChanged(state, { payload }) {
      if (!payload || Object.keys(payload).length === 0) {
        return state;
      }

      const actionInstances = [...(state.currentItem?.actionInstances || [])];

      const {
        approvers,
        stepStatus,
        iterationResultId,
        actionInstanceStatus,
        processInstanceStatus,
        currentActionInstanceId,
        currentActionInstanceStatus,
        previousActionInstance,
        currentActionInstance,
        actionInstanceId,
        status,
        markPreviousCompleted,
      } = payload || {};

      if (
        approvers &&
        (_.isEqual(approvers, state.currentItem?.processInstanceApprovers) ||
          JSON.stringify(approvers) === JSON.stringify(state.currentItem?.processInstanceApprovers))
      ) {
        return state;
      }

      if (currentActionInstance) {
        const isSameMessage = _.find(
          actionInstances,
          (action) => action.id === currentActionInstance.id,
        );

        if (isSameMessage) {
          if (markPreviousCompleted && previousActionInstance?.id) {
            const prevIndex = _.findIndex(actionInstances, { id: previousActionInstance.id });
            if (prevIndex !== -1) {
              actionInstances[prevIndex] = {
                ...actionInstances[prevIndex],
                status: CoreEnums.ActionInstanceStatus.Finished,
                endDate: new Date().toISOString(),
              };
            }
          }

          return {
            ...state,
            currentItem: {
              ...state.currentItem,
              actionInstances,
            },
          };
        }

        actionInstances.push(currentActionInstance);

        if (markPreviousCompleted && previousActionInstance?.id) {
          const prevIndex = _.findIndex(actionInstances, { id: previousActionInstance.id });
          if (prevIndex !== -1) {
            actionInstances[prevIndex] = {
              ...actionInstances[prevIndex],
              status: CoreEnums.ActionInstanceStatus.Finished,
              endDate: new Date().toISOString(),
            };
          }
        }
      }

      let index = -1;

      if (!approvers && (currentActionInstanceId || actionInstanceId || previousActionInstance)) {
        index = _.findIndex(actionInstances, {
          id: currentActionInstanceId || actionInstanceId || previousActionInstance?.id,
        });

        if (index !== -1) {
          if (
            actionInstances[index].status === actionInstanceStatus ||
            actionInstances[index].status === currentActionInstanceStatus
          ) {
            return state; // .. is same message, ignore
          }

          actionInstances[index] = {
            ...actionInstances[index],
            ...previousActionInstance,
            ...((!_.isNil(actionInstanceStatus) || !_.isNil(currentActionInstanceStatus)) && {
              status: actionInstanceStatus || currentActionInstanceStatus,
            }),
          };
        }
      }

      const stepStatusChanged =
        iterationResultId && state.stepStatusChanged?.status !== (status || stepStatus)
          ? { id: iterationResultId, status: status || stepStatus }
          : null;

      const stepAdvanced =
        !!currentActionInstance?.id &&
        currentActionInstance.id !== state.currentItem?.currentActionInstanceId;

      const newState = {
        ...state,
        // Unlock detail action buttons once the current step actually advances.
        ...(stepAdvanced && { executedCommand: null }),
        stepStatusChanged,
        currentItem: {
          ...state.currentItem,
          ...(approvers && { processInstanceApprovers: approvers }),
          ...(!_.isNil(processInstanceStatus) && { status: processInstanceStatus }),
          ...(currentActionInstance && {
            actionInstances, // Updated array with new action
            currentActionInstance,
            currentActionInstanceId: currentActionInstance.id,
            status: currentActionInstance.status,
          }),
          // Even if no currentActionInstance, update actionInstances if modified
          ...(!currentActionInstance &&
            actionInstances.length !== state.currentItem?.actionInstances?.length && {
              actionInstances,
            }),
          // Drop previous-step commands and mark them stale until fetchById
          // tags the new step — prevents Approve/Reject flashing on the next card.
          ...(stepAdvanced && {
            commands: [],
            commandsActionInstanceId: null,
          }),
        },
      };

      return newState;
    },

    queryEventTrace(state, { payload }) {
      return {
        ...state,
        emailEventTrace: payload,
      };
    },

    queryCustomActiveTab(state, { payload }) {
      return {
        ...state,
        customActiveTab: payload ?? null,
      };
    },

    queryInstanceActionType(state, { payload }) {
      return {
        ...state,
        instanceActionType: payload ?? false,
      };
    },

    queryActionTypeResult(state, { payload }) {
      return {
        ...state,
        [payload.source]: {
          data: payload?.items ?? payload?.item ?? [],
          pagination: {
            ...state[payload.source]?.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    clearActionTypeResult(state, { payload }) {
      const newState = { ...state };
      if (payload?.id) {
        delete newState[payload.id];
      }
      return newState;
    },

    queryEmailEventTraceResult(state, { payload }) {
      return {
        ...state,
        emailEventTraceResult: payload?.items || [],
        emailEventTraceResultPagination: {
          ...state.emailEventTracePagination,
          ...toPaginate(payload),
        },
      };
    },

    queryAutoOpenEditEnabled(state, { payload }) {
      return {
        ...state,
        autoOpenEditOperation: payload,
      };
    },

    cleanupSelect(state) {
      return {
        ...state,
        selectItem: null,
        ...notices,
      };
    },

    cleanupCurrent(state) {
      return {
        ...state,
        currentItem: null,
        selectItem: null,
        formConfig: null,
        completeFormConfig: null,
        continueValidationErrors: [],
        autoOpenEditOperation: false,
        executedCommand: null,
        bulkTaskItemData: [],
        bulkTaskItemPagination: meta,
        ...notices,
      };
    },

    queryCleanup(state, { payload }) {
      if (payload?.source) {
        return {
          ..._.omit(state, [payload.source]),
        };
      }
      return initialState;
    },
  },
};

export default Model;
